import React, { useMemo, useId } from 'react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';
import classNames from 'classnames';

/**
 * Accessible grouped dropdown styled for FlexCOA (primary accent, soft surfaces).
 * Built on Headless UI Listbox: keyboard navigation, focus management, portal to body.
 *
 * @param {object} props
 * @param {string} props.value - Selected option value
 * @param {(v: string) => void} props.onChange
 * @param {Array<{ id: string, label: string, icon?: React.ComponentType<{ className?: string }>, options: Array<{ value: string, label: string, description?: string }> }>} props.groups
 * @param {boolean} [props.disabled]
 * @param {string} [props.placeholder]
 * @param {string} [props.label] - Visible label above control
 * @param {string} [props.hint] - Muted subtext under label
 * @param {string} [props.className] - Wrapper
 * @param {string} [props.buttonClassName] - Trigger button
 */
export default function GroupedListboxSelect({
  value,
  onChange,
  groups,
  disabled = false,
  placeholder = 'Select…',
  label,
  hint,
  className,
  buttonClassName,
}) {
  const autoId = useId();
  const labelId = label ? `${autoId}-label` : undefined;

  const selected = useMemo(() => {
    for (const g of groups) {
      const o = g.options.find((x) => x.value === value);
      if (o) return { ...o, groupId: g.id };
    }
    return null;
  }, [groups, value]);

  return (
    <div className={classNames('w-full', className)}>
      {label && (
        <div className="mb-2" id={labelId}>
          <div className="text-xs font-semibold tracking-wide text-gray-800">{label}</div>
          {hint ? <p className="mt-0.5 text-[10px] leading-snug text-gray-500">{hint}</p> : null}
        </div>
      )}
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <ListboxButton
            aria-labelledby={labelId}
            className={classNames(
              'group relative w-full cursor-default rounded-xl border-2 border-gray-200/90 bg-white py-2.5 pl-3.5 pr-10 text-left shadow-sm transition-all duration-200 dark:border-gray-700 dark:bg-gray-800 dark:shadow-gray-900/20',
              'hover:border-primary-300/80 hover:shadow-md dark:hover:shadow-gray-900/20',
              'focus:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-200/90',
              'aria-expanded:border-primary-400 aria-expanded:shadow-md aria-expanded:ring-2 aria-expanded:ring-primary-200/80 dark:aria-expanded:shadow-gray-900/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              buttonClassName
            )}
          >
            <span className="block min-h-[2.25rem] pr-1">
              {selected ? (
                <>
                  <span className="block truncate text-sm font-medium leading-tight text-gray-900">
                    {selected.label}
                  </span>
                  {selected.description ? (
                    <span className="mt-0.5 block truncate text-[11px] font-normal tabular-nums text-gray-500">
                      {selected.description}
                    </span>
                  ) : null}
                </>
              ) : (
                <span className="block py-0.5 text-sm text-gray-500">{placeholder}</span>
              )}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
              <ChevronDown
                className="h-4 w-4 text-gray-400 transition-transform duration-200 group-aria-expanded:rotate-180 group-aria-expanded:text-primary-600"
                aria-hidden
              />
            </span>
          </ListboxButton>

          <ListboxOptions
            modal={false}
            anchor="bottom start"
            className={classNames(
              'z-[10050] !max-h-[min(24rem,70vh)] min-w-[12rem] w-[var(--button-width)] origin-top overflow-auto rounded-xl',
              'border border-gray-200/90 bg-white py-1.5 shadow-xl shadow-gray-900/[0.08] ring-1 ring-black/[0.04] dark:border-gray-700 dark:bg-gray-800 dark:shadow-gray-900/20',
              '[--anchor-gap:6px]'
            )}
          >
            {groups.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.id} className="px-1.5 pb-2 last:pb-0">
                  <div
                    className="flex items-center gap-1.5 px-2.5 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400"
                    aria-hidden
                  >
                    {Icon ? <Icon className="h-3 w-3 text-primary-500/90" strokeWidth={2} /> : null}
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {group.options.map((opt) => (
                      <ListboxOption
                        key={opt.value}
                        value={opt.value}
                        className={({ focus, selected: isSel }) =>
                          classNames(
                            'relative cursor-pointer select-none rounded-lg py-2 pl-3 pr-9 text-xs text-gray-800 transition-colors',
                            focus && 'bg-primary-50 text-primary-950',
                            isSel && 'bg-primary-50/90 font-medium text-primary-900'
                          )
                        }
                      >
                        {({ selected: isSel }) => (
                          <>
                            <span className="block truncate pr-6">{opt.label}</span>
                            {opt.description ? (
                              <span className="mt-0.5 block truncate text-[10px] font-normal tabular-nums text-gray-500">
                                {opt.description}
                              </span>
                            ) : null}
                            {isSel ? (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-primary-600">
                                <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </div>
                </div>
              );
            })}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  );
}
